import { FC, useState } from "react";

import { showAddSectionModal } from "../helpers";

import { LoadingScreen } from "components/LoadingScreen";
import { EditableField } from "components/hover-widgets/EditableField";
import { AdminSectionListPanel } from "components/materials/home/admin/content/AdminSectionList";
import { TermOptionsMenu } from "components/menus/TermOptionsMenu";
import { TermType } from "components/server-types";
import { t } from "i18n/i18n";
import { AccordionTitle } from "mds/components/AccordionTitle";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon, CalendarIcon, EyeNotVisibleIcon, FolderIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch } from "store/index";
import { selectGroupingsWithCoursesByTerm } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { ADMIN_TERM_EVENT_PREFIX } from "utils/amplitude";
import { getCourseDisplayTitle } from "utils/course";
import { onTitleChanged } from "utils/store-utils";

type SectionDetailCourseViewProps = {
  term: TermType;
  canEdit: boolean;
};

export const SectionDetailCourseView: FC<SectionDetailCourseViewProps> = ({ term, canEdit }) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const { canAuthorOrg } = useCapabilities();
  const groupedCourses = useListSelector((s) => selectGroupingsWithCoursesByTerm(s, term?.id));

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  if (!term) {
    return <LoadingScreen>{t("home.settings.no_terms")}</LoadingScreen>;
  }

  const handleAddSection = () =>
    showAddSectionModal({
      defaultTermId: term.id,
      eventName: `${ADMIN_TERM_EVENT_PREFIX}Add Section`,
    });

  return (
    <div className="px-0 py-3 text-black-tint-20 md:px-7">
      <h2 className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {term.archived_at ? (
            <EyeNotVisibleIcon className="text-blue" size="l" />
          ) : (
            <CalendarIcon className="text-blue" size="l" />
          )}

          <div className="flex items-center">
            <EditableField
              isEditing={isEditing}
              label={t("glossary.term_title")}
              textSize="h2"
              value={
                term.archived_at && !isEditing
                  ? t("home.terms.archived_term_format", { term: term.title })
                  : term.title
              }
              onSetIsEditing={setIsEditing}
              onValueChanged={(newTitle) => onTitleChanged("terms", term.id, newTitle)}
            />
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <Button
              eventName={`${ADMIN_TERM_EVENT_PREFIX}Add Section`}
              kind="gray"
              size="s"
              onClick={handleAddSection}
            >
              <IconText iconStart={<AddPlusIcon />} text={t("common.add.section")} />
            </Button>

            {canAuthorOrg && (
              <TermOptionsMenu
                eventPrefix={ADMIN_TERM_EVENT_PREFIX}
                term={term}
                onRename={() => setIsEditing(true)}
              />
            )}
          </div>
        )}
      </h2>

      {!groupedCourses.length ? (
        <Banner
          buttonChildren={
            canEdit ? (
              <SquareButton
                eventName={`${ADMIN_TERM_EVENT_PREFIX}Add Section`}
                kind="accent"
                size="s"
                onClick={handleAddSection}
              >
                <IconText iconStart={<AddPlusIcon />} text={t("common.add.section")} />
              </SquareButton>
            ) : (
              <SquareButton
                eventName={`${ADMIN_TERM_EVENT_PREFIX}Enable Edit Mode`}
                kind="accent"
                size="s"
                onClick={handleEnableEditMode}
              >
                {t("warning.enable_edit_mode")}
              </SquareButton>
            )
          }
          kind="info"
        >
          {canEdit
            ? t("home.terms.empty_term_detail_banner_text")
            : t("home.terms.empty_term_detail_banner_text_no_edit")}
        </Banner>
      ) : (
        groupedCourses.map((groupingWithCourses, index) => (
          <div key={groupingWithCourses.id || index}>
            {groupingWithCourses.id ? (
              <AccordionTitle
                headerClassName="ml-2 w-[calc(100%-12px)] justify-between border-b border-black-tint-90 pr-4 mb-3"
                iconDirection="end"
                title={
                  <div className="flex items-center gap-2">
                    <FolderIcon className="text-blue" size="m" />
                    <span className="body-m font-medium">{groupingWithCourses.title}</span>
                  </div>
                }
                startOpen
              >
                <ul>
                  {groupingWithCourses.courses.map((course) => (
                    <li className="ml-8" key={course.id}>
                      <AdminSectionListPanel
                        canEdit={canEdit}
                        eventPrefix={ADMIN_TERM_EVENT_PREFIX}
                        eventProperties={{ course: course.code, term: term.title }}
                        sections={course.sections}
                        title={getCourseDisplayTitle(course)}
                      />
                    </li>
                  ))}
                </ul>
              </AccordionTitle>
            ) : (
              <ul>
                {groupingWithCourses.courses.map((course) => (
                  <li className="ml-2" key={course.id}>
                    <AdminSectionListPanel
                      canEdit={canEdit}
                      eventPrefix={ADMIN_TERM_EVENT_PREFIX}
                      eventProperties={{ course: course.code, term: term.title }}
                      sections={course.sections}
                      title={getCourseDisplayTitle(course)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      )}
    </div>
  );
};
